import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useHistory } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import { capture } from "../../instrument";
import { INPUT_CLASS_NAME } from "../../utils";
import PageLoadingSpinner from "../../components/PageLoadingSpinner";

const AddClinic = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [laboratory, setLaboratory] = useState("");
  const [clinicianEmails, setClinicianEmails] = useState("");

  const {
    data: laboratories = [],
    isLoading: isLoadingLaboratories,
    isError: isErrorLaboratories,
  } = useQuery({
    queryKey: ["laboratories"],
    queryFn: async () => {
      const response = await API.get("/admin/laboratory");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });

  useEffect(() => {
    if (!isErrorLaboratories) return;
    capture(new Error("Failed to load laboratories for clinic creation"));
    toast.error("Erreur lors du chargement des laboratoires");
    history.push("/clinic");
  }, [isErrorLaboratories]);

  const createMutation = useMutation({
    mutationFn: (body) => API.post("/clinic", body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Clinique créée avec succès");
      queryClient.invalidateQueries({ queryKey: ["clinics"] });
      history.push("/clinic");
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de la création de la clinique");
    },
  });

  const createClinic = () => {
    if (!name) return toast.error("Le nom de la clinique est requis");
    if (!laboratory) return toast.error("Le laboratoire est requis");

    const emails = clinicianEmails
      .split(/[,\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    createMutation.mutate({ name, laboratory, clinicianEmails: emails });
  };

  if (isLoadingLaboratories) {
    return <PageLoadingSpinner />;
  }

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">Ajouter une clinique</h1>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            onClick={() => history.push("/clinic")}
          >
            Annuler
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={createClinic}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Chargement..." : "Ajouter"}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-4xl px-8 py-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">Général</h3>

            <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Nom de la clinique</label>
                <input className={INPUT_CLASS_NAME} placeholder="Nom de la clinique" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Laboratoire</label>
                <select className={INPUT_CLASS_NAME} value={laboratory} onChange={(e) => setLaboratory(e.target.value)}>
                  <option value="">Sélectionner un laboratoire</option>
                  {laboratories.map((lab) => (
                    <option key={lab._id} value={lab._id}>
                      {lab.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <h3 className="mb-4 mt-8 text-lg font-medium text-gray-900">Praticiens</h3>

            <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
              <div className="flex items-start gap-4 px-5 py-3.5">
                <div className="w-44 shrink-0">
                  <span className="text-[13px] font-medium text-gray-600">Inviter des praticiens</span>
                  <span className="block text-xs text-gray-500">Séparez les adresses email par des virgules ou des retours à la ligne</span>
                </div>
                <textarea
                  className={`${INPUT_CLASS_NAME} h-24`}
                  placeholder="email1@example.com, email2@example.com, ..."
                  value={clinicianEmails}
                  onChange={(e) => setClinicianEmails(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddClinic;
